import React, { Component } from 'react';
import gql from 'graphql-tag';
import { compose, graphql, withApollo } from 'react-apollo';
import { Helmet } from 'react-helmet';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { createStructuredSelector } from 'reselect';
import { SUBSCRIPTION_PREFERRED_LOCATION_CHANGED } from 'containers/DesktopNotificationTray';
import ErrorBoundary from 'containers/ErrorBoundary';
import {
  toggleLeaderboardOpen,
  updateLeaderboardOpen
} from 'containers/GamePage/actions';
import { selectLeaderboardOpen } from 'containers/GamePage/selectors';
import { COMPLETED_TAB, UPCOMING_TAB } from 'containers/TournamentList';
import CloseButton from 'components/CloseButton';
import ErrorNotificationPage from 'components/ErrorNotificationPage';
import Game from 'components/Game';
import GameChat from 'components/GameChat';
import MiniLeaderboard from 'components/MiniLeaderboard';
import TournamentLeaderboard from 'components/TournamentLeaderboard';
import BackgroundMusic from 'utils/BackgroundMusic';
import RootClasses from 'utils/RootClasses';
import TimeSync from 'utils/TimeSync';
import TimeSyncWrapper from 'utils/TimeSync/TimeSyncWrapper';
import { LG_BREAKPOINT_AND_UP } from 'utils/constants';
import { fromIso9075DateString } from 'utils/dates';
import { getUserData } from 'utils/graphQLFunctions';
import fragments from 'utils/graphql/fragments';
import { getGameMaximized, setGameMaximized } from 'utils/localStorage';
import {
  TOURNAMENT_EVENT_END,
  TOURNAMENT_EVENT_FINALIZE,
  TOURNAMENT_EVENT_START,
  TOURNAMENT_STATUS_COMPLETED,
  TOURNAMENT_STATUS_IN_PROGRESS,
  TOURNAMENT_STATUS_UPCOMING,
  isTournamentQueueAvailable
} from 'utils/tournaments';
import withMediaQuery from 'utils/withMediaQuery';
import ChatIcon from 'resources/Footer-Icon-Chat.png';
import IconMaximize from 'resources/Icon-Maximize.png';
import IconMinimize from 'resources/Icon-Minimize.png';
import LeaderdBoardIconActive from 'resources/Button-Leaderboard-Active.png';
import TournamentMessage from './TournamentMessage';
import './TournamentPage.css';

const TRANSITION_MS = 450;

class TournamentPage extends Component {
  constructor(props) {
    super(props);

    this.state = {
      tournamentUpdate: {
        leaderBoard: [],
        score: {}
      },
      notificationMessage: '',
      notificationRedirect: undefined,
      balanceZero: false,
      tournamentEnded: false,
      tournamentFinalized: false,
      maximized: getGameMaximized(),
      transitionState: ''
    };

    this.onTournamentEnd = this.onTournamentEnd.bind(this);
    this.onTournamentFinalize = this.onTournamentFinalize.bind(this);
    this.onGameStartError = this.onGameStartError.bind(this);
    this.onGamingSessionUpdated = this.onGamingSessionUpdated.bind(this);
    this.onWagerEvent = this.onWagerEvent.bind(this);
    this.transitionMaximize = this.transitionMaximize.bind(this);

    this.unsubscribeFromTournamentEvents = () => {};
    this.tournamentUpdatesSubscription = null;
    this.tournamentWagerEventsSubscription = null;
    this.transitionTimeout = null;
  }

  componentDidMount() {
    this.unsubscribeFromTournamentEvents =
      this.props.subscribeToTournamentEvents();
    this.subscribeToTournamentUpdates();
    this.unsubscribeToLocationData = this.props.subscribeToLocationData();
  }

  componentWillUnmount() {
    if (this.transitionTimeout) {
      clearTimeout(this.transitionTimeout);
      this.transitionTimeout = null;
    }

    this.unsubscribeFromTournamentEvents();
    this.unsubscribeFromTournamentEvents = () => {};

    this.unsubscribeToLocationData && this.unsubscribeToLocationData();

    if (this.tournamentUpdatesSubscription) {
      this.tournamentUpdatesSubscription.unsubscribe();
      this.tournamentUpdatesSubscription = null;
    }

    if (this.tournamentWagerEventsSubscription) {
      this.tournamentWagerEventsSubscription.unsubscribe();
      this.tournamentWagerEventsSubscription = null;
    }
  }

  onTournamentEnd() {
    this.setState({ tournamentEnded: true });
  }

  onTournamentFinalize() {
    this.setState({ tournamentEnded: true, tournamentFinalized: true });
  }

  handleUserPreferredLocationUpdate = () => {
    this.props.history.push('/tournaments');
  };

  componentDidUpdate(prevProps) {
    const { tournamentData, userData } = this.props;
    const { userData: prevUserData } = prevProps;
    const { tournament } = tournamentData;

    const prevPreferred = prevUserData?.user?.preferredLocation;
    const currentPreferred = userData?.user?.preferredLocation;

    if (prevPreferred !== undefined && currentPreferred !== undefined) {
      if (prevPreferred !== currentPreferred) {
        this.handleUserPreferredLocationUpdate();
      }
    }

    if (tournamentData !== prevProps.tournamentData) {
      if (!tournament) {
        return;
      }

      let newState = null;
      if (tournament.status === TOURNAMENT_STATUS_COMPLETED) {
        newState = {
          notificationMessage: 'This tournament is already complete.',
          notificationRedirect: `/tournaments?status=${COMPLETED_TAB}`
        };
      } else if (
        tournament.status === TOURNAMENT_STATUS_UPCOMING &&
        !isTournamentQueueAvailable(tournament, true)
      ) {
        newState = {
          notificationMessage: 'This tournament has not started yet.',
          notificationRedirect: `/tournaments?status=${UPCOMING_TAB}`
        };
      }

      if (newState) {
        this.setState(newState);
      }
    }
  }

  onGameStartError(message) {
    this.setState({
      notificationMessage: message
    });
  }

  onGamingSessionUpdated(gamingSession) {
    if (gamingSession) {
      const sessionIdString = gamingSession.sessionId.toString();
      this.subscribeToWagerEvents(sessionIdString);
    }
  }

  transitionMaximize(setMaximized) {
    if (this.state.maximized !== setMaximized && !this.state.transitionState) {
      setGameMaximized(setMaximized);
      this.setState({ transitionState: 'in' });
      this.transitionTimeout = setTimeout(() => {
        this.setState({ maximized: setMaximized, transitionState: 'out' });
        this.transitionTimeout = setTimeout(() => {
          this.setState({ transitionState: '' });
          this.transitionTimeout = null;
        }, TRANSITION_MS);
      }, TRANSITION_MS);
    }
  }

  subscribeToTournamentUpdates() {
    if (this.tournamentUpdatesSubscription) {
      this.tournamentUpdatesSubscription.unsubscribe();
    }
    const tournamentId = this.props.match.params.tournament;
    const that = this;
    this.tournamentUpdatesSubscription = this.props.client
      .subscribe({
        query: SUBSCRIPTION_TOURNAMENT_UPDATES,
        variables: { input: { tournamentId: tournamentId } }
      })
      .subscribe({
        next(payload) {
          that.onTournamentUpdate(payload.data);
        },
        error(err) {
          console.error('Error in TournamentUpdate: ', err);
        }
      });
  }

  onTournamentUpdate(data) {
    const { tournamentId, leaderBoard, score, totalPlayers, event } = data;

    const prev = this.state.tournamentUpdate;
    const matches = this.props.match.params.tournament === String(tournamentId);
    const unchanged =
      !leaderBoard &&
      !score &&
      (!totalPlayers || totalPlayers === prev.totalPlayers) &&
      (!event || event === prev.event);

    if (!matches || unchanged || prev.event === TOURNAMENT_EVENT_FINALIZE) {
      return;
    }

    if (event === TOURNAMENT_EVENT_END) {
      this.onTournamentEnd();
    } else if (event === TOURNAMENT_EVENT_FINALIZE) {
      this.onTournamentFinalize();
    }

    const newData = Object.assign({}, prev);

    if (leaderBoard) {
      newData.leaderBoard = leaderBoard;
    }
    if (score) {
      newData.score = score;
    }
    if (totalPlayers || totalPlayers === 0) {
      newData.totalPlayers = totalPlayers;
    }
    newData.event = event;

    this.setState({ tournamentUpdate: newData });
  }

  subscribeToWagerEvents(gamingSessionId) {
    if (this.tournamentWagerEventsSubscription) {
      this.tournamentWagerEventsSubscription.unsubscribe();
    }
    const that = this;
    this.tournamentWagerEventsSubscription = this.props.client
      .subscribe({
        query: SUBSCRIPTION_TOURNAMENT_WAGER_EVENTS,
        variables: { input: { gameUrlSessionId: gamingSessionId } }
      })
      .subscribe({
        next(payload) {
          that.onWagerEvent(payload.data && payload.data.balance);
        },
        error(err) {
          console.error('Error in TournamentWagerEvents: ', err);
        }
      });
  }

  onWagerEvent(balance) {
    if (balance !== undefined) {
      const balanceZero = balance <= 0;
      if (balanceZero !== this.state.balanceZero) {
        this.setState({ balanceZero });
      }
    }
  }

  render() {
    const { tournament } = this.props.tournamentData;
    const { maximized, transitionState, notificationMessage } = this.state;
    const maxClass = maximized ? ' game-maximized' : '';
    const rootClass = maximized ? 'darken game-maximized' : 'darken';
    const tournamentAvailable =
      tournament && tournament.status !== TOURNAMENT_STATUS_COMPLETED;

    if (notificationMessage) {
      return (
        <ErrorNotificationPage
          notificationMessage={notificationMessage}
          redirectUrl={this.state.notificationRedirect}
        />
      );
    }

    return (
      <div className="tournament-page">
        <Helmet>
          <title>Tournament</title>
        </Helmet>
        <RootClasses className={rootClass} />
        <BackgroundMusic priority={2} options={null} />
        <div className={`page-container tournament-container${maxClass}`}>
          {tournamentAvailable ? (
            this.generateContent()
          ) : (
            <div className="tournament-wrapper" />
          )}
        </div>
        <div className={`game-maximize-transition ${transitionState}`} />
      </div>
    );
  }

  generateContent() {
    const { user, loading: userLoading } = this.props.userData;
    const { leaderboardOpen, toggleLeaderboardOpen } = this.props;

    const { tournament, loading: tournamentLoading } =
      this.props.tournamentData;

    const {
      tournamentUpdate,
      balanceZero,
      tournamentEnded,
      tournamentFinalized,
      maximized
    } = this.state;

    const tournamentUpcoming = tournament.status === TOURNAMENT_STATUS_UPCOMING;

    const tournamentMillis = tournament
      ? fromIso9075DateString(
          tournamentUpcoming ? tournament.startTime : tournament.endTime
        ).valueOf()
      : Date.now();
    const countdownTo = TimeSync.toLocalTime(tournamentMillis);

    const rightPanel = !maximized && (
      <div className="page-right-panel">
        {!userLoading && !tournamentLoading ? (
          <div className={leaderboardOpen && 'hide'}>
            <TournamentLeaderboard
              tournament={tournament}
              userScore={tournamentUpdate.score}
              totalPlayers={tournamentUpdate.totalPlayers}
              user={user}
              countdownTo={countdownTo}
              ranks={tournamentUpdate.leaderBoard}
            />
          </div>
        ) : null}
        <div className={!leaderboardOpen && 'hide'}>
          <GameChat
            room={`trm_${this.props.match.params.tournament}`}
            title="Tournament Chat"
          />
        </div>
      </div>
    );

    const gameHeader = !maximized ? (
      <div className="tournament-details-wrapper">
        {tournament.userInGroup && !this.props.matchesBreakpoint && (
          <MiniLeaderboard
            tournament={tournament}
            userScore={tournamentUpdate.score}
            countdownTo={countdownTo}
          />
        )}
        <button
          className="toggle-leaderboard-btn"
          onClick={toggleLeaderboardOpen}
          type="button"
        >
          <img
            src={leaderboardOpen ? LeaderdBoardIconActive : ChatIcon}
            alt={'Open Leaderboard'}
          />
        </button>
        <button
          type="button"
          className="maximize-button"
          onClick={() => this.transitionMaximize(true)}
        >
          <img src={IconMaximize} alt="Maximize" />
        </button>
        <CloseButton url="/tournaments" />
      </div>
    ) : (
      <div>
        {tournament.userInGroup && (
          <MiniLeaderboard
            tournament={tournament}
            userScore={tournamentUpdate.score}
            countdownTo={countdownTo}
          />
        )}
        <button
          type="button"
          className="minimize-button"
          onClick={() => this.transitionMaximize(false)}
        >
          <img src={IconMinimize} alt="Minimize" />
        </button>
        <CloseButton url="/tournaments" />
      </div>
    );

    const hideGame = tournamentEnded || !tournament.userInGroup || balanceZero;
    let gameArea = !tournamentLoading && (
      <ErrorBoundary>
        {!hideGame && (
          <Game
            isTournament
            game={tournament.game}
            tournament={tournament}
            onError={this.onGameStartError}
            onGamingSessionUpdated={this.onGamingSessionUpdated}
          />
        )}
        {(hideGame || tournamentUpcoming) && (
          <TournamentMessage
            rankings={tournamentUpdate.leaderBoard}
            balanceZero={balanceZero}
            ended={tournamentEnded}
            finalized={tournamentFinalized}
            tournament={tournament}
            countdownTo={countdownTo}
          />
        )}
      </ErrorBoundary>
    );

    return (
      <div className="tournament-wrapper">
        <h1 className="sr-only">Tournament: {tournament.name}</h1>
        <div className="page-left-panel">
          {gameHeader}
          <div className="game-area">{gameArea}</div>
        </div>
        {rightPanel}
      </div>
    );
  }
}

const QUERY_GET_TOURNAMENT = gql`
  query GetTournament($tournament: GetTournament!) {
    tournament(tournament: $tournament) {
      ...Tournament
    }
  }
  ${fragments.tournament}
`;

const SUBSCRIPTION_TOURNAMENT_UPDATES = gql`
  subscription TournamentUpdates($input: TournamentUpdatesInput!) {
    tournamentUpdates(input: $input) {
      tournamentId
      totalPlayers
      leaderBoard {
        player {
          displayName
          userId
          avatarUrl
          avatarData
        }
        score
        balance
        rank
      }
      score {
        score
        rank
      }
      event
    }
  }
`;

const SUBSCRIPTION_TOURNAMENT_WAGER_EVENTS = gql`
  subscription TournamentWagerEvents($input: TournamentWagerEventsInput!) {
    tournamentWagerEvents(input: $input) {
      balance
    }
  }
`;

const SUBSCRIPTION_TOURNAMENT_EVENTS = gql`
  subscription TournamentEvents {
    tournamentEvents {
      id
      event
    }
  }
`;

const TournamentPageWithData = compose(
  getUserData({
    name: 'userData',
    props: ({ userData }) => {
      return {
        userData,
        subscribeToLocationData: () => {
          return userData.subscribeToMore({
            document: SUBSCRIPTION_PREFERRED_LOCATION_CHANGED,
            updateQuery: (prev, { subscriptionData }) => {
              if (
                subscriptionData?.data?.newPreferredLocation &&
                subscriptionData.data.newPreferredLocation !==
                  prev.user.preferredLocation
              ) {
                return {
                  ...prev,
                  user: {
                    ...prev.user,
                    preferredLocation:
                      subscriptionData.data.newPreferredLocation
                  }
                };
              }
              return prev;
            }
          });
        }
      };
    }
  }),
  graphql(QUERY_GET_TOURNAMENT, {
    name: 'tournamentData',
    options: ({ match }) => ({
      variables: { tournament: { id: match.params.tournament } },
      fetchPolicy: 'network-only'
    }),
    props: ({ tournamentData }) => ({
      tournamentData,
      subscribeToTournamentEvents: () => {
        return tournamentData.subscribeToMore({
          document: SUBSCRIPTION_TOURNAMENT_EVENTS,
          updateQuery: (prev, { subscriptionData }) => {
            const { id, event } = subscriptionData.data;
            const { tournament } = prev;

            if (
              tournament &&
              id === tournament.id &&
              event === TOURNAMENT_EVENT_START
            ) {
              const newTournament = Object.assign({}, tournament, {
                status: TOURNAMENT_STATUS_IN_PROGRESS
              });
              return Object.assign({}, prev, { tournament: newTournament });
            }

            return null;
          }
        });
      }
    })
  })
)(TournamentPage);

const mapStateToProps = createStructuredSelector({
  leaderboardOpen: selectLeaderboardOpen()
});

const mapDispatchToProps = dispatch => ({
  setLeaderboardOpen: open => dispatch(updateLeaderboardOpen(open)),
  toggleLeaderboardOpen: () => dispatch(toggleLeaderboardOpen())
});

const withConnect = connect(mapStateToProps, mapDispatchToProps);

export default compose(
  withMediaQuery(LG_BREAKPOINT_AND_UP),
  withApollo,
  withConnect,
  withRouter,
  TimeSyncWrapper
)(TournamentPageWithData);
